import React, { useEffect, useMemo, useRef, useState } from "react"

import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"
import {
    COMMON_ONE_VERIFY_POLICIES_CONFIG,
    PoliciesListType,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config.ts"
import { IPolicyColumnConfiguration } from "@common-shared/types/policies.types.ts"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element.tsx"
import { SpinnerElement } from "@front-shared/elements/spinner.element.tsx"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook.ts"

import { GENERAL_CONFIG } from "../../config/general.config"
import {
    POLICIES_CONFIG,
    PolicyHeaderDragDirection,
} from "../../config/policies.config"
import { ROUTES_CONFIG } from "../../config/routes.config"
import { PoliciesListHelper } from "../../helpers/policiesList.helper"
import { useHasPermissions } from "../../hooks/hasPermissions.hook"
import { useIsFeatureFlagEnabled } from "../../hooks/isFeatureFlagEnabled.hook"
import { usePoliciesList } from "../../hooks/policiesList.hook.ts"
import { useAppSelector } from "../../store/selectors/app.selector"
import { selectedPracticeConfigurationSelector } from "../../store/selectors/selectedPracticeConfiguration.selector.ts"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { PoliciesListComponent } from "../policies/policiesList/policiesList.component"
import { PoliciesListHeaderComponent } from "../policies/policiesList/policiesListHeader.component"
import { TaskAssignmentAssigneePopupComponent } from "../popups/taskAssignmentAssigneePopup.component.tsx"
import { TaskAssignmentUnassignPopupComponent } from "../popups/taskAssignmentUnassignPopup.component.tsx"
import { BulkTaskAssignmentControlComponent } from "./bulkTaskAssignmentControl.component.tsx"

export const AssignedTasksComponent: React.FunctionComponent = () => {
    useScrollFix()

    useHasPermissions(OneVerifyPermission.workTaskAssignments)

    const canManageTaskAssignments = useHasPermissions(
        OneVerifyPermission.manageTaskAssignments,
        undefined,
        false,
    )

    useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_TASKS,
        undefined,
        ROUTES_CONFIG.profileUrl,
    )

    const {
        // state
        selectedPolicies,
        setSelectedPolicies,
        // functions
        onPoliciesSelected,
        onPoliciesDeselected,
        onDeselectAllPolicies,
    } = usePoliciesList()

    const scrollableTableRef = useRef<HTMLDivElement>(null)

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const selectedPracticeConfiguration = useAppSelector(
        selectedPracticeConfigurationSelector,
    )

    const [isAssignmentPopupActive, setIsAssignmentPopupActive] =
        useState<boolean>(false)

    const [isUnassignPopupActive, setIsUnassignPopupActive] =
        useState<boolean>(false)

    // This component controls them and passes to list for display and header for modifications
    const [columnsConfiguration, setColumnsConfiguration] = useState<
        IPolicyColumnConfiguration[]
    >([])

    // Sets list type based on workflow management version
    const listType = useMemo<PoliciesListType | null>(() => {
        if (!selectedPracticeConfiguration) {
            return null
        }

        return COMMON_ONE_VERIFY_POLICIES_CONFIG.assignedTasksListTypeVariant[
            selectedPracticeConfiguration.workflowManagementVersion
        ]
    }, [selectedPracticeConfiguration?.workflowManagementVersion])

    // Change columns configurations once practice role modalities change
    useEffect(() => {
        if (!selectedPracticeRole || !listType) {
            return
        }

        setColumnsConfiguration(
            PoliciesListHelper.getColumnsConfiguration(
                listType,
                selectedPracticeRole.availableModalities,
            ),
        )
    }, [selectedPracticeRole?.availableModalities, listType])

    const onSaveColumnsConfiguration = (
        columns: IPolicyColumnConfiguration[],
    ) => {
        if (!listType) {
            return
        }

        setColumnsConfiguration(columns)

        PoliciesListHelper.saveColumnsConfigurations(
            listType,
            columns,
            selectedPracticeRole?.availableModalities,
        )
    }

    const onChangeColumnOrder = (
        columnLabelMoveSource: string,
        columnLabelMoveTarget: string,
        direction: PolicyHeaderDragDirection,
    ) => {
        const newColumns = PoliciesListHelper.changeSingleColumnOrdering(
            columnsConfiguration,
            columnLabelMoveSource,
            columnLabelMoveTarget,
            direction,
        )

        onSaveColumnsConfiguration(newColumns)
    }

    // manager: can select all policies
    // biller: can select only "active" policies (to change status)
    const isSinglePolicySelectable = (item: IOneVerifyCoverageCheckHistory) => {
        return (
            canManageTaskAssignments ||
            !!(
                item.policy?.latestAssignment?.status &&
                POLICIES_CONFIG.activeAssignmentStatuses.includes(
                    item.policy.latestAssignment.status,
                )
            )
        )
    }

    if (!listType) {
        return <SpinnerElement containerClassName="mt-[32px] text-center" />
    }

    return (
        <div className="pb-[128px] relative">
            <PageHelmetElement
                title={POLICIES_CONFIG.headerTitleMapping[listType]}
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            <PoliciesListHeaderComponent
                className="mb-[24px]"
                listType={listType}
                setSelectedPolicies={setSelectedPolicies}
                scrollableTableRef={scrollableTableRef}
                columnsConfiguration={columnsConfiguration}
                onColumnsConfigurationChange={onSaveColumnsConfiguration}
                withExport
                withSavingFilterPresets
            />

            {columnsConfiguration.length ? (
                <PoliciesListComponent
                    scrollableElementRef={scrollableTableRef}
                    listType={listType}
                    sortEnabled={true}
                    columns={columnsConfiguration}
                    policiesSelectable={true}
                    selectedItems={selectedPolicies}
                    onSelectedItems={onPoliciesSelected}
                    onDeselectedItems={onPoliciesDeselected}
                    onDeselectedAllItems={onDeselectAllPolicies}
                    onChangeColumnOrder={onChangeColumnOrder}
                    isSinglePolicySelectable={isSinglePolicySelectable}
                />
            ) : null}

            <BulkTaskAssignmentControlComponent
                listType={listType}
                onCancel={onDeselectAllPolicies}
                coverageChecks={selectedPolicies}
                withBulkStatus
                withBulkPriority={!!canManageTaskAssignments}
                withBulkAssign={!!canManageTaskAssignments}
                onBulkAssign={() => {
                    setIsAssignmentPopupActive(true)
                }}
                onBulkUnassign={() => {
                    setIsUnassignPopupActive(true)
                }}
            />

            {isAssignmentPopupActive && (
                <TaskAssignmentAssigneePopupComponent
                    coverageChecksToAssign={selectedPolicies}
                    policiesListType={listType}
                    onDone={() => {
                        onDeselectAllPolicies()
                        setIsAssignmentPopupActive(false)
                    }}
                    isActive
                />
            )}

            {isUnassignPopupActive && (
                <TaskAssignmentUnassignPopupComponent
                    coverageChecks={selectedPolicies}
                    policiesListType={listType}
                    onDone={() => {
                        onDeselectAllPolicies()
                        setIsUnassignPopupActive(false)
                    }}
                    isActive
                />
            )}
        </div>
    )
}
